import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Edit, Trash, CheckCircle, XCircle, AlertCircle, TrendingUp, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { startOfWeek, addDays, addWeeks, subWeeks, isFuture, isToday, isSameDay, format } from 'date-fns';
import { User } from '../../types';
import TimesheetEntryForm from './TimesheetEntryForm';
import { todayLocal, toLocalDateString } from '../../utils/localDate';
import { toast } from '../../utils/toast';

const TARGET_HOURS = 7.5;

interface MyTimesheetsProps {
  currentUser: User;
}

const MyTimesheets: React.FC<MyTimesheetsProps> = ({ currentUser }) => {
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    startDate: toLocalDateString(new Date(new Date().setMonth(new Date().getMonth() - 1))),
    endDate: todayLocal(),
    status: 'all'
  });
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [newEntryDate, setNewEntryDate] = useState<string | undefined>(undefined);

  // Weekly grid state (Zoho Sprints-style: a week of day columns, navigable)
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [weekEntries, setWeekEntries] = useState<any[]>([]);
  const [weekLoading, setWeekLoading] = useState(true);

  useEffect(() => {
    loadTimesheets();
    loadSummary();
  }, [currentUser.id, filter]);

  useEffect(() => {
    loadWeekEntries();
  }, [currentUser.id, weekStart]);

  const loadWeekEntries = async () => {
    setWeekLoading(true);
    try {
      const start = format(weekStart, 'yyyy-MM-dd');
      const end = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      const response = await fetch(`/api/timesheets/user/${currentUser.id}?startDate=${start}&endDate=${end}`);
      if (response.ok) {
        setWeekEntries(await response.json());
      }
    } catch (err) {
      console.error('Error loading week entries:', err);
    } finally {
      setWeekLoading(false);
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEntryFor = (day: Date) => weekEntries.find((e) => isSameDay(new Date(e.date), day));
  const weekTotalHours = weekEntries.reduce((sum, e) => sum + e.hoursWorked, 0);

  const openEntryForDay = (day: Date) => {
    const entry = weekEntryFor(day);
    if (entry) {
      setEditingEntry(entry);
    } else if (!isFuture(day)) {
      setNewEntryDate(format(day, 'yyyy-MM-dd'));
      setShowForm(true);
    }
  };

  const refreshAll = () => {
    loadTimesheets();
    loadSummary();
    loadWeekEntries();
  };

  const loadTimesheets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: filter.startDate,
        endDate: filter.endDate,
        ...(filter.status !== 'all' && { status: filter.status })
      });

      const response = await fetch(`/api/timesheets/user/${currentUser.id}?${params}`);
      if (response.ok) {
        const data = await response.json();
        setTimesheets(data);
      }
    } catch (err) {
      console.error('Error loading timesheets:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      // Backend expects a single "month" param in YYYY-MM format, not
      // separate year/month params — this mismatch previously made every
      // summary query match nothing, so the stats cards always read zero.
      const month = filter.endDate.slice(0, 7);
      const response = await fetch(`/api/timesheets/summary/${currentUser.id}?month=${month}`);
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this timesheet entry?')) return;

    try {
      const response = await fetch(`/api/timesheets/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast('✅ Timesheet deleted successfully');
        refreshAll();
      } else {
        const errorData = await response.json();
        toast(`❌ ${errorData.error || 'Failed to delete timesheet'}`);
      }
    } catch (err) {
      toast('❌ Failed to delete timesheet');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      rejected: 'bg-rose-100 text-rose-800 border-rose-300',
      revised: 'bg-sky-100 text-sky-800 border-sky-300'
    };

    const icons = {
      pending: <AlertCircle className="w-4 h-4" />,
      approved: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />,
      revised: <Edit className="w-4 h-4" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${styles[status as keyof typeof styles]}`}>
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getStatusAccent = (status: string) => {
    const accents = {
      pending: 'border-l-4 border-l-amber-400',
      approved: 'border-l-4 border-l-emerald-400',
      rejected: 'border-l-4 border-l-rose-400',
      revised: 'border-l-4 border-l-sky-400'
    };
    return accents[status as keyof typeof accents] || '';
  };

  if (showForm || editingEntry) {
    return (
      <div>
        <button
          onClick={() => {
            setShowForm(false);
            setEditingEntry(null);
            setNewEntryDate(undefined);
          }}
          className="mb-4 px-4 py-2 text-blue-600 hover:text-blue-800"
        >
          ← Back to My Timesheets
        </button>
        <TimesheetEntryForm
          currentUser={currentUser}
          existingEntry={editingEntry}
          initialDate={newEntryDate}
          onSuccess={() => {
            setShowForm(false);
            setEditingEntry(null);
            setNewEntryDate(undefined);
            refreshAll();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-6 text-white">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Clock className="w-7 h-7" />
            My Timesheets
          </h1>
          <p className="text-blue-100 mt-1">Track and submit your daily work hours</p>
        </div>
        <button
          onClick={() => { setNewEntryDate(undefined); setShowForm(true); }}
          className="px-4 py-2 bg-white text-blue-700 rounded-lg hover:bg-blue-50 font-medium shadow-sm shrink-0"
        >
          + New Entry
        </button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md shadow-blue-200 p-4 text-white">
            <div className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 rounded-full bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/15">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-blue-100">Total Hours</p>
                <p className="text-2xl font-bold">{summary.totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg shadow-md shadow-violet-200 p-4 text-white">
            <div className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 rounded-full bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/15">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-violet-100">Days Logged</p>
                <p className="text-2xl font-bold">{summary.daysLogged}</p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-md shadow-amber-200 p-4 text-white">
            <div className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 rounded-full bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/15">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-amber-100">Avg Hours/Day</p>
                <p className="text-2xl font-bold">{summary.avgHours.toFixed(1)}h</p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-md shadow-emerald-200 p-4 text-white">
            <div className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 rounded-full bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/15">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-emerald-100">Approved</p>
                <p className="text-2xl font-bold">{summary.approvedCount}/{summary.daysLogged}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Timesheet Grid */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setWeekStart(subWeeks(weekStart, 1))}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
              title="Previous week"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 min-w-[220px] text-center">
              {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
            </h2>
            <button
              onClick={() => setWeekStart(addWeeks(weekStart, 1))}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
              title="Next week"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
              className="ml-2 px-3 py-1 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
            >
              This Week
            </button>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Week Total</p>
            <p className={`text-xl font-bold ${weekTotalHours >= TARGET_HOURS * 5 ? 'text-emerald-600' : 'text-gray-900'}`}>
              {weekTotalHours.toFixed(1)}h
            </p>
          </div>
        </div>

        {weekLoading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading week…</div>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day) => {
              const entry = weekEntryFor(day);
              const future = isFuture(day) && !isToday(day);
              const meetsTarget = entry && entry.hoursWorked >= TARGET_HOURS;

              const cellClasses = entry
                ? meetsTarget
                  ? 'bg-emerald-50 border-emerald-300 hover:border-emerald-400'
                  : 'bg-amber-50 border-amber-300 hover:border-amber-400'
                : future
                  ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                  : 'bg-white border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50';

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => openEntryForDay(day)}
                  disabled={future}
                  className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 p-3 min-h-[92px] transition-colors ${cellClasses} ${isToday(day) ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
                >
                  <span className="text-xs font-medium text-gray-500 uppercase">{format(day, 'EEE')}</span>
                  <span className="text-sm font-semibold text-gray-700">{format(day, 'd')}</span>
                  {entry ? (
                    <span className={`text-base font-bold ${meetsTarget ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {entry.hoursWorked}h
                    </span>
                  ) : future ? (
                    <span className="text-xs text-gray-400">—</span>
                  ) : (
                    <Plus className="w-4 h-4 text-blue-500" />
                  )}
                  {entry && (
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      entry.status === 'approved' ? 'bg-emerald-500' :
                      entry.status === 'rejected' ? 'bg-rose-500' :
                      entry.status === 'revised' ? 'bg-sky-500' : 'bg-amber-500'
                    }`} title={entry.status} />
                  )}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-3">Click a day to add an entry, or an existing one to view/edit it.</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filter.startDate}
              onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filter.endDate}
              onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="revised">Needs Revision</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={loadTimesheets}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Timesheets List */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : timesheets.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No timesheets found for the selected filters</p>
          <button
            onClick={() => { setNewEntryDate(undefined); setShowForm(true); }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Submit Your First Entry
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {timesheets.map((entry) => (
            <div key={entry._id} className={`bg-white rounded-lg shadow-md p-6 ${getStatusAccent(entry.status)}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="text-lg font-bold text-gray-800">
                      {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                    </h3>
                    {getStatusBadge(entry.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {entry.hoursWorked}h
                      {entry.hoursWorked >= 7.5 ? (
                        <span className="text-emerald-600">✓</span>
                      ) : (
                        <span className="text-amber-600">⚠</span>
                      )}
                    </span>
                    {entry.reportingTo && (
                      <span>Reported to: {entry.reportingTo.name}</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  {(entry.status === 'pending' || entry.status === 'revised') && (
                    <>
                      <button
                        onClick={() => setEditingEntry(entry)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(entry._id)}
                        className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Work Description:</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{entry.workDescription}</p>
                </div>

                {entry.projects && entry.projects.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Projects:</h4>
                    <div className="flex flex-wrap gap-2">
                      {entry.projects.map((project: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {project}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {entry.taskLinks && entry.taskLinks.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Task Links:</h4>
                    <div className="space-y-1">
                      {entry.taskLinks.map((link: any, idx: number) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-blue-600 hover:underline text-sm"
                        >
                          {link.title}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {entry.reviewComments && (
                  <div className={`p-3 rounded-lg ${
                    entry.status === 'approved' ? 'bg-emerald-50' :
                    entry.status === 'rejected' ? 'bg-rose-50' :
                    'bg-sky-50'
                  }`}>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Review Comments:</h4>
                    <p className="text-gray-600 text-sm">{entry.reviewComments}</p>
                    {entry.reviewedBy && (
                      <p className="text-xs text-gray-500 mt-1">
                        - {entry.reviewedBy.name} on {new Date(entry.reviewedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTimesheets;
